import { useState } from 'react';
import { Lock, Sparkles } from 'lucide-react';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import UpgradeModal from './UpgradeModal';

/**
 * Wraps any feature and shows a locked overlay if the user's tier doesn't include it.
 *
 * Two modes:
 *   <FeatureGate feature="launchVolumeForecaster">    — full-page blur overlay
 *     <LaunchVolumeForecasterPage />
 *   </FeatureGate>
 *
 *   <FeatureGate feature="copyTrading" inline>         — inline lock icon
 *     <span>Copy Trading</span>
 *   </FeatureGate>
 */
export default function FeatureGate({ feature, children, fallback, inline = false }) {
  const { hasAccess, requiredTierConfig } = useFeatureAccess(feature);
  const [showUpgrade, setShowUpgrade] = useState(false);

  if (hasAccess) return <>{children}</>;
  if (fallback) return <>{fallback}</>;

  // Inline mode — small lock badge next to content
  if (inline) {
    return (
      <>
        <button
          onClick={() => setShowUpgrade(true)}
          className="flex items-center gap-1.5 opacity-50 hover:opacity-80 cursor-pointer transition-opacity"
        >
          <Lock className="w-3.5 h-3.5 text-gray-500" />
          {children}
        </button>
        {showUpgrade && <UpgradeModal featureKey={feature} onClose={() => setShowUpgrade(false)} />}
      </>
    );
  }

  // Full-page mode — blurred content with lock overlay
  const tierLabel = requiredTierConfig?.label || 'Elite';
  const tierColor = requiredTierConfig?.color || 'text-amber-400';

  return (
    <>
      <div className="relative rounded-2xl overflow-hidden">
        {/* Blurred content preview */}
        <div className="blur-[6px] pointer-events-none select-none opacity-40">
          {children}
        </div>

        {/* Lock overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-dark-900/60 backdrop-blur-sm rounded-2xl">
          <div className="text-center max-w-sm px-6">
            <div className="w-16 h-16 rounded-2xl bg-dark-700 border border-dark-600 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-amber-400" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className={`w-4 h-4 ${tierColor}`} />
              <span className={`text-xs font-bold tracking-widest uppercase ${tierColor}`}>
                {tierLabel} Feature
              </span>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">
              Unlock this feature
            </h3>
            <p className="text-sm text-gray-400 mb-5">
              Upgrade to {tierLabel} to access this feature and take your trading to the next level.
            </p>
            <button
              onClick={() => setShowUpgrade(true)}
              className="px-6 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 hover:from-amber-300 hover:to-orange-300 transition-all"
            >
              View Upgrade Options
            </button>
          </div>
        </div>
      </div>

      {showUpgrade && <UpgradeModal featureKey={feature} onClose={() => setShowUpgrade(false)} />}
    </>
  );
}
